import {
  clearSession,
  IPublicSession,
  isSessionOrganizer,
  resetSession,
  sharedSessionSchema,
  updatePublicSession,
  updatePublicSessionNow,
  userSession,
} from 'state/session';
import { user } from 'utils/auth/user';
import { State } from 'utils/state';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { markDirty } from './dirtyKeys';
import { isApplyingRemote } from './remoteApplyGuard';

export function syncToServer(state: State<IPublicSession>) {
  state.on('change', (keys) => {
    // For local AVE sessions, we rely on the syncing prStrings via the broadcast, as we cannot leverage 3D Showroom prString change events as it is not rendered in all tabs (local AVEs only support on tab/stream at a time)
    const updateEverything =
      integrationType() === IntegrationType.WebComponent &&
      (userSession().get('activeExperience') === '2D' || userSession().isLocalAveSession);
    // Block syncing when offline (except for offline flag changes)
    if (userSession().get('offline') && !keys.includes('offline')) {
      console.warn('Session sync blocked: offline mode is enabled');
      return;
    }

    // do not proceed if session is starting or ending
    if (keys.includes('id') || keys.includes('endedAt')) {
      if (keys.includes('endedAt') && integrationType() === IntegrationType.WebComponent) {
        // Reset ended session for web component case (handled outside of wc context in standalone)
        clearSession();
        resetSession();
      }
      // AVS-4080: When endedAt arrives in state (e.g. loadPublicSession resolved with a
      // soft-deleted scheduled session), cancel any pending debounced PUT. Otherwise a
      // pre-load brand/market write queued by contextLoader will fire ~1s later with the
      // ended payload, the lambda rejects with 404 SESSION_NOT_FOUND, and sendApiRequest
      // renders a duplicate red toast on top of the page's own error UI.
      if (keys.includes('endedAt')) {
        updatePublicSession.cancel();
      }
      return;
    }

    // AVS-4080: Skip syncing once the session is ended. Loading a soft-deleted
    // scheduled session populates state with endedAt; downstream listeners (e.g.
    // initCarSelectionMethod) then mutate other fields and would otherwise trigger
    // a PUT that includes endedAt in the body. Cancel any pending debounce here too
    // in case it slipped past the change-with-endedAt branch above.
    if (userSession().get('endedAt')) {
      updatePublicSession.cancel();
      return;
    }

    // Customer should never sync aveSessionId to server (dealer-only field)
    // This prevents race conditions where customer's initial load overwrites dealer's value
    if (!user.isDealer && (keys.includes('aveSessionId') || keys.includes('aveChannelName'))) {
      console.warn('Customer cannot sync aveSessionId or aveChannelName to server. Skipping update.');
      return;
    }

    // Only the session organizer can change activeExperience (AVS-4141)
    // Non-organizer dealers must follow the organizer's experience choice
    if (user.isDealer && keys.includes('activeExperience') && !isSessionOrganizer()) {
      console.warn('Non-organizer cannot sync activeExperience to server. Skipping update.');
      return;
    }

    if (userSession().get('isShared') && (keys.includes('brand') || keys.includes('market'))) {
      console.warn('Changes to brand or market while sharing are not allowed. Skipping session update.');
      return;
    }

    // A remote apply (broadcast / reconnect read-repair) must not be treated as a local edit: it
    // must neither mark keys dirty nor echo a write back to the server (AVS-4330). The lifecycle
    // and permission guards above still run for remote-origin changes (e.g. an `endedAt` broadcast
    // still tears the session down) — only the sync side-effects below are suppressed.
    if (isApplyingRemote()) {
      return;
    }

    const sessionValues = userSession().get();
    // Only guard while in 3D: experienceSwitchingSanitation intentionally clears both AVE ids
    // atomically with a 3D->2D switch, and streamActive can stay true across that switch (a
    // live session can keep streaming while showing 2D content), so streamActive alone can't
    // distinguish a legitimate reset from a stale clobber.
    const shouldStripEmptyLiveStreamingIdentifier =
      user.isDealer &&
      sessionValues.streamActive !== false &&
      !sessionValues.endedAt &&
      sessionValues.activeExperience === '3D';
    const shouldStripEmptyAveSessionId =
      shouldStripEmptyLiveStreamingIdentifier && keys.includes('aveSessionId') && sessionValues.aveSessionId === '';
    const shouldStripEmptyAveChannelName =
      shouldStripEmptyLiveStreamingIdentifier && keys.includes('aveChannelName') && sessionValues.aveChannelName === '';

    let keysToSync = [...keys];
    let syncData: Partial<IPublicSession> | undefined;

    if (shouldStripEmptyAveSessionId || shouldStripEmptyAveChannelName) {
      syncData = { ...sessionValues };
      const guardedKeys: string[] = [];
      if (shouldStripEmptyAveSessionId) {
        delete syncData.aveSessionId;
        keysToSync = keysToSync.filter((key) => key !== 'aveSessionId');
        guardedKeys.push('aveSessionId');
      }
      if (shouldStripEmptyAveChannelName) {
        delete syncData.aveChannelName;
        keysToSync = keysToSync.filter((key) => key !== 'aveChannelName');
        guardedKeys.push('aveChannelName');
      }
      console.warn('Clobber guard fired: dropped empty live-stream identifier(s) from outgoing sync', guardedKeys);
    } else if (
      user.isDealer &&
      ((keys.includes('aveSessionId') && sessionValues.aveSessionId === '') ||
        (keys.includes('aveChannelName') && sessionValues.aveChannelName === ''))
    ) {
      console.info(
        'Clobber guard evaluated: empty live-stream identifier allowed to sync (session ending, not streaming, or not in 3D)',
        {
          streamActive: sessionValues.streamActive,
          endedAt: sessionValues.endedAt,
          activeExperience: sessionValues.activeExperience,
        }
      );
    }

    // only keys that are part of the internal session schema should trigger an update on database level
    // if none of those keys changed, we can skip the database update and just emit the event to the participants
    const relevantKeys = Object.keys(sharedSessionSchema.shape);

    // Mark locally-edited synced keys unconfirmed until the server acknowledges them, so an
    // incoming payload cannot overwrite an edit that is still in flight (AVS-4330).
    markDirty((keysToSync as (keyof IPublicSession)[]).filter((key) => relevantKeys.includes(String(key))));

    const syncOptions = syncData ? { updateEverything, data: syncData } : { updateEverything };

    if (!relevantKeys.some((key) => keysToSync.includes(key))) {
      if (keysToSync.length === 0) {
        return;
      }
      console.warn('No relevant keys changed for session update: ', keysToSync);
      updatePublicSession({ emitOnly: true, ...syncOptions });
      return;
    }

    // aveSessionId is critical for multi-dealer sync — bypass the debounce and send
    // immediately. This ensures a joining dealer reads the correct aveSessionId from
    // the server before creating its own AVE session. (AVS-4141)
    // Note: activeExperience intentionally uses the debounce to avoid a last-writer-wins
    // race between two dealers' immediate PUTs.
    if (keysToSync.includes('aveSessionId')) {
      updatePublicSession.cancel();
      updatePublicSessionNow(syncOptions);
      return;
    }

    // send session data to the server (debounced)
    updatePublicSession(syncOptions);
  });
}
